/* Which world the map is (requirement 35, M1 and M13).

   There are three: L is the world the game has always been played on, 200 × 100; M (120 × 60)
   and S (80 × 40) are the same Earth shrunk by area overlap (`scripts/shrink_world_map.py`), with
   the same land share, the same oceans and poles, and the same 2:1 shape. A lobby and a game name
   theirs in `map_size`; every payload from before the sizes existed is L, because it was.

   **One world is active at a time**, and every module that used to read `worldData.json` at
   import — the canvas, the minimap, the terrain, the fog, the route builder, the ETA, the cell
   actions — reads `activeWorld()` at call time instead. The lobby view sets it from the lobby's
   size, the game view from the game's (the replay's game included), and anything with no game
   reads L. `Canvas` and `MiniMap` are keyed by the size, so a change of world remounts them
   rather than leaving a buffer sized for the last one.

   All three are imported up front: S and M together are ~120KB of JSON beside L's 270KB, and the
   map is the app, so there is nothing to defer them past (the same call `cityNames.json` made). */

import worldL from "src/assets/json/worldData.json"
import worldM from "src/assets/json/worldData_M.json"
import worldS from "src/assets/json/worldData_S.json"
import capitalsL from "src/assets/json/capitalCells.json"
import capitalsM from "src/assets/json/capitalCells_M.json"
import capitalsS from "src/assets/json/capitalCells_S.json"
import namesL from "src/assets/json/cityNames.json"
import namesM from "src/assets/json/cityNames_M.json"
import namesS from "src/assets/json/cityNames_S.json"

export const MAP_SIZES = ["S", "M", "L"]
export const DEFAULT_MAP_SIZE = "L"

const build = (size, data, capitals, cityNames) => ({
  size,
  columns: data.dimensions.columns,
  rows: data.dimensions.rows,
  // "col-row" -> country code; a missing key is water, as on the server.
  matrix: data.matrix,
  countries: data.countries,
  countryFlags: data.countryFlags,
  capitals: new Set(capitals),
  cityNames,
})

const WORLDS = {
  S: build("S", worldS, capitalsS, namesS),
  M: build("M", worldM, capitalsM, namesM),
  L: build("L", worldL, capitalsL, namesL),
}

/* A size off a payload: "S" | "M" | "L", anything else (absent, an older server) is L. */
export const mapSizeOf = (size) => (WORLDS[size] ? size : DEFAULT_MAP_SIZE)

export const worldFor = (size) => WORLDS[mapSizeOf(size)]

let active = WORLDS[DEFAULT_MAP_SIZE]

/* Make `size` the world every reader sees. Idempotent and cheap, so a view may call it on every
   render, before its children read the map. */
export const setActiveWorld = (size) => {
  active = worldFor(size)
  return active
}

export const activeWorld = () => active

/* The cylinder's arithmetic, on the active world: columns wrap, rows do not. */
export const wrapColumn = (column) => {
  const { columns } = active
  return ((column % columns) + columns) % columns
}

export const columnDistance = (a, b) => {
  const across = Math.abs(a - b) % active.columns
  return Math.min(across, active.columns - across)
}
